/**
 * 條件式日誌工具
 * 根據環境自動判斷是否輸出日誌
 * 生產環境下不會輸出敏感資訊到 console
 */

// 強制只在 localhost 啟用日誌,其他所有環境都禁用
const isDevelopment = false  // 暫時強制關閉所有日誌用於測試

// 如果需要重新啟用本地開發日誌,取消下面的註解:
// const isDevelopment = (
//   window.location.hostname === 'localhost' ||
//   window.location.hostname === '127.0.0.1'
// )

/**
 * 日誌級別配置
 */
const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
}

/**
 * 格式化日誌訊息
 * @param {string} level - 日誌級別
 * @param {string} message - 日誌訊息
 * @param {*} data - 附加資料
 * @returns {string} 格式化後的日誌
 */
function formatLog(level, message, data) {
  const timestamp = new Date().toISOString()
  const prefix = `[${timestamp}] [${level.toUpperCase()}]`
  
  if (data !== undefined) {
    return `${prefix} ${message}`
  }
  return `${prefix} ${message}`
}

/**
 * 清理敏感資訊
 * @param {*} data - 原始資料
 * @returns {*} 清理後的資料
 */
function sanitizeData(data) {
  if (!data || typeof data !== 'object') {
    return data
  }

  const sensitiveKeys = ['password', 'token', 'apiKey', 'secret', 'authorization']
  const sanitized = Array.isArray(data) ? [...data] : { ...data }

  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase()
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '***REDACTED***'
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      sanitized[key] = sanitizeData(sanitized[key])
    }
  })

  return sanitized
}

/**
 * 開發環境日誌 - 輸出詳細資訊
 */
const logger = {
  /**
   * Debug 級別日誌
   */
  debug: (message, data) => {
    if (isDevelopment) {
      console.debug(formatLog(LogLevel.DEBUG, message), data !== undefined ? data : '')
    }
  },

  /**
   * Info 級別日誌
   */
  info: (message, data) => {
    if (isDevelopment) {
      console.info(formatLog(LogLevel.INFO, message), data !== undefined ? data : '')
    }
  },

  /**
   * Warning 級別日誌
   */
  warn: (message, data) => {
    if (isDevelopment) {
      console.warn(formatLog(LogLevel.WARN, message), data !== undefined ? sanitizeData(data) : '')
    }
    // 生產環境不輸出警告日誌
  },

  /**
   * Error 級別日誌 - 僅開發環境輸出
   */
  error: (message, error) => {
    if (isDevelopment) {
      console.error(formatLog(LogLevel.ERROR, message), error)
    }
    // 生產環境不輸出錯誤日誌到 console
    // 如需監控錯誤，應使用專門的錯誤追蹤服務（如 Sentry）
  },

  /**
   * API 請求日誌 - 開發環境專用
   */
  apiRequest: (method, url, payload) => {
    if (isDevelopment) {
      console.group(`🌐 API Request: ${method} ${url}`)
      console.log('Payload:', sanitizeData(payload))
      console.groupEnd()
    }
  },

  /**
   * API 回應日誌 - 開發環境專用
   */
  apiResponse: (method, url, response) => {
    if (isDevelopment) {
      console.group(`✅ API Response: ${method} ${url}`)
      console.log('Data:', sanitizeData(response))
      console.groupEnd()
    }
  },

  /**
   * 使用者操作日誌
   */
  userAction: (action, details) => {
    if (isDevelopment) {
      console.log(`👤 User Action: ${action}`, sanitizeData(details))
    }
  },

  /**
   * 效能監控日誌
   */
  performance: (label, duration) => {
    if (isDevelopment) {
      console.log(`⏱️ Performance: ${label} took ${duration}ms`)
    }
  }
}

/**
 * 生產環境監控工具 - 可整合 Application Insights 等服務
 */
export const productionLogger = {
  /**
   * 記錄錯誤到遠端監控服務
   */
  logError: async (error, context) => {
    if (!isDevelopment) {
      // TODO: 整合 Azure Application Insights
      // 可以發送到遠端日誌服務
      try {
        // 範例：發送到後端日誌 API
        // await fetch('/api/logs/error', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     message: error.message,
        //     stack: error.stack,
        //     context: sanitizeData(context)
        //   })
        // })
      } catch (e) {
        // 靜默失敗，避免日誌記錄本身造成問題
      }
    }
  },

  /**
   * 記錄效能指標
   */
  logPerformance: async (metric) => {
    if (!isDevelopment) {
      // TODO: 發送效能指標到監控服務
    }
  }
}

export default logger
